'use strict'; 

let util = require('../core/util'); 
let JCRUtil = require('./JCRUtil'); 
let {PRC, validateOrPredict, postProcessDecomposedAttributeValidation, productRulesEngine, typeCastUserSelection} = require('./prc');
let CartUtility=require('../order/CartUtility');

/**
 * @class ProductService
 */
module.exports = class ProductService {

    constructor(param) {
        const init = util.buildInitParam(param); 

        this.LANG = init.lang;
        this.EVENT_PRODUCT_FETCHED = 'product:fetched';
        this.EVENT_PRICING_FETCHED = 'pricing-v3:fetched';

        this._EventBus = init._EventBus;
        
        this._getProductUrl = function(productId) {
            return `${init.baseUrl + init.restPrefix + init.backboneGatewayPrefix}products/v2/products/${productId}/normalized/expanded`; 
        }; 
        this._getWorkspaceJCRUrl = function(workspace, pagePath) {
            return `${ init.baseUrl + init.restPrefix }nodes/v1/${workspace}/${pagePath.replace(/^\//, '')}`; 
        };

        this._getExclusionRulesUrl = function(productTypeId) {
            return init.baseUrl + init.restPrefix + init.backboneGatewayPrefix + 'products/product-types/' + productTypeId + '/exclusion-rules'; 
        };

        this._getTenantDefinitionUrl = function(productTypeId) {
            return init.baseUrl + init.restPrefix + init.backboneGatewayPrefix + 'products/product-types/' + productTypeId + '/tenant-definition'; 
        };
        
        this._productDiscountsUrl = function(id){
            return init.baseUrl + init.restPrefix + init.abcGatewayPrefix + 'product-discounts' +(id ? '/' + id : '');
        }

        //this._getPricingUrl = init.baseUrl + init.restPrefix + init.backboneGatewayPrefix + 'pricing/v4/priceShippingDateList';
        this._getPricingUrlByQuantity = init.baseUrl + init.restPrefix + init.backboneGatewayPrefix + 'pricing/v4/priceQuantityList';
        this._getPricingVerboseUrl = init.baseUrl + init.restPrefix + init.backboneGatewayPrefix + 'pricing/v3/verbose';

        this._getTaxesUrl = init.baseUrl + init.restPrefix + init.backboneGatewayPrefix + 'tax/v3/draftswithkey';
        
        this._getFileCopyUrl = init.baseUrl + init.restPrefix + init.backboneGatewayPrefix + 'filecopy/v1/fileCopy';

        this._getArtworkPricingUrl = init.baseUrl + init.restPrefix + init.backboneGatewayPrefix + 'ruleengine/ruleengine/rules-multiple';
        
        this._getTierDiscountUrl = init.baseUrl + init.restPrefix + init.backboneGatewayPrefix + 'tier/v1/calcTierDiscount';

        this._getRuleValidationsUrl = init.ruleEngineGateway + 'v4/validations';
        
        this._searchProductUrl = init.baseUrl + init.restPrefix + init.abcGatewayPrefix + 'product-projections/search';

        this._shippingRecalculateArrivalDateUrl = `${init.baseUrl + init.restPrefix + init.backboneGatewayPrefix}shipping/v1/recalculatearrivaldate`;

    }
    
    /**
     * Get Product By Id or Key
     * @param {String} productIdOrKey 
     * @param {Object} params 
     */
    getProduct(productIdOrKey, params) {
        return util.makeGETRequest(this._getProductUrl(productIdOrKey), params).then(this._dispatchProductIsFetched.bind(this));
    }

    /**
     * Search Products via `Product Projection Search`
     * @param {String|null} text 
     * @param {String|null} language 
     * @param {Array} filters 
     * @param {Array} filterQueries 
     * @param {Number} limit 
     * @param {Number} offset 
     * @param {String|null|Array} sort 
     * @param {Boolean} fuzzy 
     */
    searchProducts(text = null, language = null, filters = [], filterQueries = [], limit = 10, offset = 0, sort = null, fuzzy = false) {
        let request = {
            'filter': filters,
            'filter.query': filterQueries,
            'limit': parseInt(limit),
            'offset': parseInt(offset),
            'fuzzy': !!fuzzy
        };
        if(util.typeOf(language, 'string') && util.typeOf(text, 'string') && language.length && text.length) {
            request[`text.${language}`] = text;
        }
        if(sort) {
            request['sort'] = sort;
        }
        
        return util.makeGETRequest(this._searchProductUrl, request, null, util.paramsSerializer);
    }

    /**
     * Dispatch Product Fetched Event
     * @param {Object} res 
     */
    _dispatchProductIsFetched(res){
        if(res.id){
            this._EventBus.dispatch(this.EVENT_PRODUCT_FETCHED, res);
        }
        return res;
    }

    /**
     * get Page/Workspace JCR, Magnolia Specific 
     * @param {String} pagePath 
     * @param {Number} depth 
     * @param {Boolean} transform 
     */
    getWorkspaceJCR(pagePath, depth = 2, transform = true, workspace = 'website') {
        return util.makeGETRequest(this._getWorkspaceJCRUrl(workspace, pagePath), { depth: depth ? depth : 2 }, { Accept:'application/json' }).then(_data => {
            if(transform) {
                return JCRUtil.transform(_data);
            }

            return _data;
        });
    }

    /**
     * get Exclusion Rule
     * @param {String} productTypeId 
     */
    getExclusionRules(productTypeId) {
        return util.makeGETRequest(this._getExclusionRulesUrl(productTypeId));
    }

    /**
     * get Tenant Definition
     * @param {String} productTypeId 
     */
    getTenantDefinition(productTypeId) {
        return util.makeGETRequest(this._getTenantDefinitionUrl(productTypeId));
    }

    /**
     * find MCP SKU from VariantInfo
     * @param {Object} variantInfo 
     */
    _getVariantSKU(variantInfo){
        let mcpSKUAttribute = variantInfo.attributes.find(a => a.class === 'mcpSKU');
        return (mcpSKUAttribute && mcpSKUAttribute.value) ? util.getAttributeFirstValue(mcpSKUAttribute.value, null) : null;
    }

    /**
     * Validate if User selection are valid or not
     * @param {{ tenantDefinition: Object, exclusionRules : Object, dataSheets: Object }} v1RuleSet
     * @param {Object} userSelections
     * @param {Array} merchantAttributeMeta
     * @returns {Promise<Object>}
     */
    validateSelection({ tenantDefinition, exclusionRules, dataSheets = {} }, userSelections, merchantAttributeMeta = []){
        let LANG = this.LANG;
        return new Promise(function (resolve, reject) {
            try{
                let ruleSet = {
                    whiteList: tenantDefinition.value,
                    rules: exclusionRules.value,
                    dataSheets: dataSheets.value
                };
                let result = productRulesEngine.validate(ruleSet, typeCastUserSelection(userSelections));
                result = postProcessDecomposedAttributeValidation(userSelections, result, ruleSet, merchantAttributeMeta, LANG);
                resolve(result); 
            }catch(e){
                reject(e);
            }
        });
    }

    /**
     *  Call this function if you want to predict value for missing attributes while having last validation result which is "isValid" .
     *
     * @param {{ tenantDefinition: Object, exclusionRules : Object, dataSheets: Object }} v1RuleSet
     * @param finalSelections
     * @param lastValidationResult Result which should be valid
     * @param {Array|Boolean} predictOnly
     * @returns {Promise<unknown>|Promise<Object>} Validation object or Error
     */
    predictValueOf({ tenantDefinition, exclusionRules, dataSheets = {} }, finalSelections, lastValidationResult, predictOnly = false) {
        let _this = this;
        let userSelections = Object.assign({}, finalSelections);
        let v1RuleSet = { tenantDefinition, exclusionRules, dataSheets };

        if (!lastValidationResult || !lastValidationResult.isValid) {
            return util.sendValidationError(new Error('INVALID_REQUEST'));
        }
        if(predictOnly && util.typeOf(predictOnly, 'array')){
            lastValidationResult.missingAttributes = (lastValidationResult.missingAttributes || [])
                .filter(attribute => predictOnly.includes(attribute.attributeKey));
        }

        (lastValidationResult.missingAttributes || []).map(attribute => {
            let missingAttribute = lastValidationResult.remainingOptions.find(option => {
                return option.attributeKey === attribute.attributeKey;
            });
            if (missingAttribute && !userSelections[missingAttribute.attributeKey] && missingAttribute.attributeValues && missingAttribute.attributeValues.length) {
                userSelections[missingAttribute.attributeKey] = missingAttribute.attributeValues[0].value;
            } else if (missingAttribute && !userSelections[missingAttribute.attributeKey] && missingAttribute.attributeRanges && missingAttribute.attributeRanges.length) {
                userSelections[missingAttribute.attributeKey] = missingAttribute.attributeRanges[0].minimum;
            }
        });

        return _this.validateSelection(v1RuleSet, userSelections).then(finalResult => {
            if (!finalResult.isValid) {
                //console.error('ABC:ERROR', 'final selection resulted in InValid selection', 'userSelections: ', JSON.stringify(userSelections), 'result:',JSON.stringify(finalResult));
                // new selection made it invalid, so need to recursively validate it with `result.violatedExclusions`
                let counter = 1,
                    limit = 5;
                return _this._validateWithViolatedRules(v1RuleSet, finalSelections, userSelections, finalResult, counter, limit);
            }
            return finalResult;
        });
    }

    /**
     * Once price & quantity is selected by user, make prepare final selection
     * @param {{ tenantDefinition: Object, exclusionRules : Object, dataSheets: Object }} v1RuleSet
     * @param {Object} finalSelections
     */
    buildFinalSelection({ tenantDefinition, exclusionRules, dataSheets = {} }, finalSelections){
        let _this = this;
        let userSelections = Object.assign({}, finalSelections);
        let v1RuleSet = { tenantDefinition, exclusionRules, dataSheets };

        return _this.validateSelection(v1RuleSet, userSelections).then(result => {
            // When selection is still not complete, try to pass missingAttributes values from `result.remainingOptions`
            if(result.isValid && !result.isComplete){
                return _this.predictValueOf(v1RuleSet, userSelections, result);
            }
            return result;
        });
    }

    /**
     * recursively validate user selection by using data from violatedExclusions
     * 
     * @param {Object} v1RuleSet
     * @param {Object} finalSelections
     * @param {Object} selections 
     * @param {Object} result 
     * @param {Number} counter 
     * @param {Number} limit 
     */
    _validateWithViolatedRules(v1RuleSet, finalSelections, selections, result, counter, limit){
        let _this = this;
        let userSelections = Object.assign({}, selections);

        // Build user selection by picking up data from violated Rules' result
        if(result.violatedExclusions && result.violatedExclusions.length){
            for(let i = 0, len = result.violatedExclusions.length; i < len; i++){
                for(let r = 0, r_len = result.violatedExclusions[i].results.length; r < r_len; r++){

                    if(finalSelections[result.violatedExclusions[i].results[r].attributeKey]){
                        console.error('ABC:ERROR', 'violatedRule causing user selection overwrite, rule name:', result.violatedExclusions[i].name, ' overwrites:', result.violatedExclusions[i].results[r].attributeKey);
                    }
                    
                    // Use min value for attribute
                    if(result.violatedExclusions[i].results[r].attributeValues && result.violatedExclusions[i].results[r].attributeValues.length){
                        userSelections[result.violatedExclusions[i].results[r].attributeKey] = result.violatedExclusions[i].results[r].attributeValues[0].value;
                    }
                    else if(result.violatedExclusions[i].results[r].attributeRanges && result.violatedExclusions[i].results[r].attributeRanges.length){
                        userSelections[result.violatedExclusions[i].results[r].attributeKey] = result.violatedExclusions[i].results[r].attributeRanges[0].minimum;
                    }
                }
            }
        }
        
        // Validate new data and if causes another violation try again
        return _this.validateSelection(v1RuleSet, userSelections).then((res) => {
            if(!res.isValid && counter < limit){
                counter++;
                return _this._validateWithViolatedRules(v1RuleSet, finalSelections, userSelections, res, counter, limit);
            }
            return res;
        });
    }

    /**
     *  Construct `merchandisedAttributeValues` for passing same into PRC initialization
     * @param {Object} productModel
     * @param {Number} variantId
     * @param {Array} userSelectableAttributes
     * @returns {Array}
     */
    getMerchandisedAttributes(productModel, variantId, userSelectableAttributes = []) {
        let lang = this.LANG;
        let allVariants = [productModel.masterData.current.masterVariant, ...productModel.masterData.current.variants];
        let variantInfo = allVariants.find(variant => { return variant.id === variantId });
        return variantInfo.attributes
            .reduce((attributes, attribute) => {
                if(userSelectableAttributes.indexOf(attribute.label[lang]) !== -1){
                    attributes.push({
                        attributeLabel: attribute.label[lang],
                        attributeValues: attribute.value
                    });
                }
                return attributes;
            }, []);
    }

    /**
     * get PRC instance
     * @param {{ tenantDefinition: Object, exclusionRules : Object, dataSheets: Object }} v1RuleSet
     * @param {Array|Boolean} merchandisedAttributeValues -> Useful to limit scope of prediction
     * @param {Array} merchantAttributeMeta
     * @returns {PRC}
     */
    getPRC({ tenantDefinition, exclusionRules, dataSheets = {} }, merchandisedAttributeValues = false, merchantAttributeMeta = []){
        return new PRC(tenantDefinition.value, exclusionRules.value, dataSheets.value, merchandisedAttributeValues, merchantAttributeMeta, this.LANG);
    }

    /**
     *  Validate selection or predict next possible one when it is invalid
     * @param PRCInstance from `getPRC` method
     * @param {Object} userSelections
     * @param {Array} selectableAttributes
     * @param {String} lastSelectedAttributeLabel
     * @returns {Promise<*>}
     */
    validateOrPredict(PRCInstance, userSelections, selectableAttributes, lastSelectedAttributeLabel) {
        return validateOrPredict(PRCInstance, userSelections, selectableAttributes, lastSelectedAttributeLabel);
    }

    /**
     * get pricing from external pricing API, v4
     * 
     * @deprecated
     * @param {String} productId 
     * @param {Array} userSelections 
     * @param {String} currencyCode 
     * @param {Object} address 
     * @param {Array} quantityList 
     * @param {String} customerGroupId 
     */
    _getPricing(productId, userSelections, currencyCode, address, quantityList = null, customerGroupId = null, cancelIndentifier = null){
        let request = {
            "currency": currencyCode,
            "variableAttributes": userSelections,
            "productId": productId,
            "address": address,
            "requestTime": (new Date()).toISOString()
        };

        if(quantityList){
            request.quantityList = quantityList;
        }
        if(util.typeOf(customerGroupId, 'string')){
            request.customerGroupId = customerGroupId;
        }
        return util.makePostRequest(this._getPricingUrl, request, null, cancelIndentifier).then(this._dispatchPricingV3IsFetched.bind(this));
    }

    /**
     * get pricing from external pricing API, v4. It is an optimized endpoint over previous one
     *
     * @param {String} productId
     * @param {Array} userSelections
     * @param {String} currencyCode
     * @param {Object} address
     * @param {Array} quantityList
     * @param {String} customerGroupId
     * @param {String} now Date ISOString
     * @param {Object} additionalParams
     */
    getPricingByQuantity(productId, userSelections, currencyCode, address, quantityList = null, customerGroupId = null, cancelIndentifier = null, now = (new Date()).toISOString(), additionalParams = {}){
        var request = {
            "currency": currencyCode,
            "variableAttributes": userSelections,
            "productId": productId,
            "address": address,
            "requestTime": now
        };

        if(quantityList) {
            request.quantityList = quantityList;
        }
        if(util.typeOf(customerGroupId, 'string')){
            request.customerGroupId = customerGroupId;
        }
        if(util.typeOf(additionalParams, 'object')){
            request = Object.assign(request, additionalParams);
        }
        return util.makePostRequest(this._getPricingUrlByQuantity, request, null, cancelIndentifier).then(this._dispatchPricingV3IsFetched.bind(this));
    }

    /**
     * get pricing verbose inputs from external pricing API, v3
     *
     * @param {String} productId
     * @param {Array} userSelections
     * @param {Array} verboseAttributes
     * @param {Integer} quantity
     */
    getPricingVerbose(productId, userSelections, verboseAttributes, quantity, mcpSku) {
        var request = {
            "variableAttributes": userSelections,
            "productId": productId,
            "verboseAttributes": verboseAttributes,
            "quantity": quantity
        };

        if (mcpSku) {
            request.mcpSku = mcpSku;
        }

        return util.makePostRequest(this._getPricingVerboseUrl, request);
    }


    /**
     * Dispatch Pricing V3 Fetched Event
     * @param {Object} res 
     */
    _dispatchPricingV3IsFetched(res){
        if(res.priceTier){
            this._EventBus.dispatch(this.EVENT_PRICING_FETCHED, res);
        }
        return res;
    }

    /**
     * get product Tax Rate Draft
     * @param {Object} productDetail 
     * @param {Array} userSelections 
     * @param {String} currencyCode 
     * @param {Object} shipToAddress 
     * @param {Object} billToAddress
     */
    getProductTaxRate(productDetail, userSelections, currencyCode, shipToAddress, billToAddress, cancelIndentifier = null){
        if(!util.typeOf(productDetail, 'object') || !productDetail.productId || !productDetail.quantity){
            return util.sendValidationError(new Error('Invalid productDetail request parameter'));
        }
        productDetail.variableAttributes = userSelections;
        let request = {
            "currency": currencyCode,
            "productDetails": [ productDetail ],
            "shipToAddress": shipToAddress
        }
        if(util.typeOf(billToAddress, 'object')){
            request.billToAddress = billToAddress;
        }
        return util.makePostRequest(this._getTaxesUrl, request, null, cancelIndentifier).then(function(res){
            if(util.typeOf(res, 'array') && res.length){
                // Valid response set
                return (res[0] && res[0].productId ? res[0] : res);
            }
            return res;
        });
    }

    /**
     * get External Tax Rate Drafts for mutiple products
     * @param {Array} productDetails 
     * @param {String} currencyCode 
     * @param {Object} shipToAddress 
     * @param {Object} billToAddress 
     */
    getProductsTaxRate(productDetails, currencyCode, shipToAddress, billToAddress, cancelIndentifier = null){
        if(!util.typeOf(productDetails, 'array')){
            return util.sendValidationError(new Error('Invalid productDetails request parameter'));
        }
        let request = {
            "currency": currencyCode,
            "productDetails": productDetails,
            "shipToAddress": shipToAddress
        }
        if(util.typeOf(billToAddress, 'object')){
            request.billToAddress = billToAddress;
        }
        return util.makePostRequest(this._getTaxesUrl, request, null, cancelIndentifier);
    }

    /**
     * get File Copy info
     * @param {Boolean} transform 
     */
    getFileCopy(transform){
        return util.makeGETRequest(this._getFileCopyUrl).then(function(res){
            if(transform && res.minOrderQuantity){
                return {
                    minOrderQuantity: res.minOrderQuantity,
                    quantity: res.quantity,
                    fee: (res.fee && res.fee.number ? res.fee.number * 100 : null),
                    currencyCode: (res.fee && res.fee.currency ? res.fee.currency.currencyCode : null),
                };
            }
            return res;
        });
    }

    /**
     * get Artwork services pricing
     * @param {Object} data 
     */
    getArtworkServicesAndPricing(data){
        return util.makePostRequest(this._getArtworkPricingUrl, data);
    }

    /**
     * get Tier Discount
     * @param {String} customerEmailId 
     * @param {String} productId 
     * @param {String} productTypeId 
     */
    getTierDiscount(customerEmailId, productId, productTypeId){
        return util.makePostRequest(this._getTierDiscountUrl, {
            "customerEmailId": customerEmailId,
            "productId": productId,
            "productTypeId": productTypeId
        });
    }

    _getProductDiscounts(){  // wrapper to product-discount thing to fetch the productDiscounts
        return util.makeGETRequest(this._productDiscountsUrl(),{
                    sort:"sortOrder asc",
                    where:`isActive="true"`
                }).then(function(results){
            return results.results; // return the results array sorted ascending by "sortOrder" and whic are active
        });
    }

    _searchPredicatesDiscounts(listOfDiscounts,dict){ // main function to fetch the product discount
        var filteredResults=[];  
        for(var i=0;i<listOfDiscounts.length;i++){
                listOfDiscounts[i].predicate=listOfDiscounts[i].predicate.replace(/\s/g,'');// replace and with logical && 
                if(listOfDiscounts[i].predicate.includes("AND") || listOfDiscounts[i].predicate.includes("and")){
                    listOfDiscounts[i].predicate=listOfDiscounts[i].predicate.replace("AND","&&");
                    listOfDiscounts[i].predicate=listOfDiscounts[i].predicate.replace("and","&&");
                }
                else if(listOfDiscounts[i].predicate.includes("OR") || listOfDiscounts[i].predicate.includes("or")){
                    listOfDiscounts[i].predicate=listOfDiscounts[i].predicate.replace("OR","||");
                    listOfDiscounts[i].predicate=listOfDiscounts[i].predicate.replace("or","||");
                }
                for (var key in dict){ //iterate over every dictionary and  replace true or false accordingly
                    listOfDiscounts[i]=this._searchForAndReplace(listOfDiscounts[i],key,dict[key]);
                }
                if(this._checkIfApplicable(listOfDiscounts[i].predicate)){ //check if it qualifies for the discount
                    filteredResults.push(listOfDiscounts[i]);
                }
        }
        // if there are many discounts that qualify than return the discounts with highest priorty or the lowest sort order
        if(filteredResults.length>0)
            return filteredResults[0];
        else
            return []; // if no results qualify  then return empty object
    }

    _searchForAndReplace(productDiscount,key,value){ //positive is product.key="" and negative is product.key!=""
        if(productDiscount.predicate.includes(key) && value && productDiscount.predicate.includes(value.positive)){ // if its present replace true
            productDiscount.predicate=productDiscount.predicate.replace(value.positive,true);
        }
        else if(productDiscount.predicate.includes(key+"!=") && value && productDiscount.predicate.includes(value.negative)){ //if the predicate for not this product key
            productDiscount.predicate=productDiscount.predicate.replace(value.negative,false);
        }
        else if(productDiscount.predicate.includes(key+"!=")){  // if predicate is not equals to other key which makes true as of condition
            productDiscount.predicate=this._replaceWith(productDiscount.predicate,key,true);
        }
        else if(productDiscount.predicate.includes(key)){ // if there is this predicate but not matches the current key
            productDiscount.predicate=this._replaceWith(productDiscount.predicate,key,false);
        }
        return productDiscount;
    }

    _checkIfApplicable(predicate){ // evaluation of predicate conatining true and false with logical && || 
        var a=eval(predicate)
        if(a)
            return a;
        else return false;
    };

    _replaceWith(predicate,key,toReplaceWith){ // function to replece the key in the predicate with toReplacewith string
        var startIndex=predicate.indexOf(key);
        var intermediate=predicate.indexOf("\"",startIndex)+1; 
        var lastIndex=predicate.indexOf("\"",intermediate)+1; // logic to find the secong " in the string 
        if(lastIndex<0)
            lastIndex=predicate.length;
        predicate=predicate.replace(predicate.substring(startIndex,lastIndex),""+toReplaceWith); 
        return predicate;
    }

    // call this for getting product discounts 
    productDiscountsapply(productkey,isLoggedIn,customerGroupKey){
        var me=this;
        var filteredResponse={};
        var toSend={};
        var dict=[];
        if(isLoggedIn)
        dict['customerGroup.key']={positive:"customerGroup.key=\""+customerGroupKey+"\"",negative:"customerGroup.key!=\""+customerGroupKey+"\""};
        else dict['customerGroup.key']={positive:"null",negative:"null"};
        dict['product.key']={positive:"product.key=\""+productkey+"\"",negative:"product.key!=\""+productkey+"\""};
        return this._getProductDiscounts().then(function(response){
            if(response.length>0)
            filteredResponse= me._searchPredicatesDiscounts(response,dict);
            else return filteredResponse;
            toSend.id=filteredResponse.id;
            toSend.value=filteredResponse.value;
            return toSend;
        },function(error){
            return error;
        });
    }

    /**
     * get Updated Arrival Date
     * 
     * @param {String} productKey 
     * @param {Number} quantity 
     * @param {String} merchantId 
     * @param {Object} finalSelections 
     * @param {Number} bufferArtworkServiceHours 
     * @param {Number} bufferProductionHours 
     * @param {Number} productionTime
     * @param {Array} carrierServiceKeys 
     * @param {Object} address 
     * @param {String} deliveryDateFromPricing 
     * @param {String} now Date ISOString
     */
    async getUpdatedArrivalDate(
        productKey, quantity, merchantId, finalSelections,
        bufferArtworkServiceHours, bufferProductionHours, productionTime,
        carrierServiceKeys,
        address = null, deliveryDateFromPricing = null,
        fulfillmentLocationId = null, productTypeId = null, now = (new Date()).toISOString(),callViaItemDelivery=0) {
        return CartUtility.getUpdatedArrivalDate(this._shippingRecalculateArrivalDateUrl,productKey, quantity, merchantId, finalSelections,
            bufferArtworkServiceHours, bufferProductionHours, productionTime,
            carrierServiceKeys,
            address, deliveryDateFromPricing,
            fulfillmentLocationId, productTypeId,now,callViaItemDelivery);
    }
}


// WEBPACK FOOTER //
// ./src/product/ProductService.js